import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const fmtDate = (iso) => (iso
  ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  : '');

// Trial countdown, "payment due" grace warning and the read-only notice.
// States come from the server (services/billing.js via /auth/me).
export default function BillingBanner() {
  const { billing, isOwner, reload } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [hitReadOnly, setHitReadOnly] = useState(false);

  // A write was refused because the account is read-only — refresh state so
  // the banner explains why.
  useEffect(() => {
    const onReadOnly = () => { setHitReadOnly(true); reload(); };
    window.addEventListener('sb:read-only', onReadOnly);
    return () => window.removeEventListener('sb:read-only', onReadOnly);
  }, [reload]);

  if (!billing || pathname === '/upgrade') return null;
  const { state, days_left, trial_ends_at, grace_ends_at, complimentary_until } = billing;

  let tone, text;
  if (state === 'read_only' || hitReadOnly) {
    tone = 'danger';
    text = isOwner
      ? 'Your subscription has lapsed, so ShopBook is read-only. Your records are safe — you can still view and export them. Pay to start recording again.'
      : 'This account is read-only until the owner renews the subscription. You can still view records.';
  } else if (state === 'grace') {
    tone = 'warning';
    text = isOwner
      ? `Payment is due. Everything keeps working until ${fmtDate(grace_ends_at)} — after that ShopBook becomes read-only until you pay.`
      : null;
  } else if (state === 'complimentary' && complimentary_until && days_left != null && days_left <= 7) {
    tone = 'info';
    text = isOwner
      ? `Your complimentary access ends ${days_left <= 1 ? 'tomorrow' : `in ${days_left} days`} (${fmtDate(complimentary_until)}).`
      : null;
  } else if (state === 'trial' && days_left != null && days_left <= 7) {
    tone = 'info';
    text = isOwner
      ? `Your free trial ends ${days_left <= 1 ? 'tomorrow' : `in ${days_left} days`} (${fmtDate(trial_ends_at)}).`
      : null;
  }
  if (!text) return null;

  const colors = {
    danger:  { bg: '#450a0a', fg: '#fecaca', border: 'var(--color-danger)' },
    warning: { bg: '#422006', fg: '#fde68a', border: 'var(--color-warning)' },
    info:    { bg: '#172554', fg: '#bfdbfe', border: 'var(--color-accent)' },
  }[tone];

  return (
    <div role="status" style={{
      background: colors.bg, color: colors.fg, borderBottom: `1px solid ${colors.border}`,
      padding: '0.6rem 1rem', fontSize: '0.85rem', display: 'flex', gap: '0.75rem',
      alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
    }}>
      <span>{text}</span>
      {isOwner && (
        <button className="btn-primary" style={{ padding: '0.35rem 0.9rem', fontSize: '0.8rem' }}
          onClick={() => navigate('/upgrade')}>
          How to pay
        </button>
      )}
    </div>
  );
}
